/**
 * One table for everything a crawler or a share sheet reads about a public
 * route: title, description, canonical URL, Open Graph and Twitter tags, and
 * schema.org JSON-LD. Two consumers:
 *
 *   - vite.config.ts writes a static HTML shell per route at build time
 *     (build/<route>/index.html) because Facebook, iMessage, Slack and X do
 *     not run scripts; the CloudFront router serves those shells.
 *   - usePageMeta keeps the same tags current during client-side navigation.
 *
 * The facts here (dates, hotel, price, fund link) are the ones confirmed on
 * 2026-09-08 and mirrored in the API seed; the live pages read them from
 * settings, this is the build-time copy. Keep this module free of browser
 * imports: it also runs in Node inside the Vite config.
 */

export const SITE_URL = 'https://brandonstrong.com';
export const SITE_NAME = 'BrandonStrong.com';

export const DEFAULT_DESCRIPTION =
  'A celebration of life for Brandon Taylor at Secrets Hideaway, Kissimmee, Saturday October 3, 2026. Free entry 7 to 10 PM. RSVP and support Shawna. #BrandonStrong';

export const EVENT = {
  name: 'Celebration of Life for Brandon Taylor',
  /**
   * Free entry opens at 7 PM (the family, 2026-09-15). Venue time (Eastern).
   * Still no end time: James confirmed the same day that the night does NOT end
   * at ten - ten is when free entry stops - and he has never said when it wraps,
   * so nothing on this site may claim one.
   */
  start: '2026-10-03T19:00:00-04:00',
  /** The venue as of 2026-09-15; the hotel block at SpringHill Suites is gone. */
  venue: {
    name: 'Secrets Hideaway',
    street: '2145 E Irlo Bronson Memorial Hwy',
    city: 'Kissimmee',
    region: 'FL',
    postalCode: '34744',
  },
  /**
   * SecretsOverflow's front page, which is an empty search form.
   *
   * NOT for linking. It exists as the fallback inside overflowBookingUrl, which
   * is what every link on this site should use: it carries the night of the
   * celebration and lands on real rooms and prices instead of a form the
   * visitor has to fill in again.
   */
  overflowUrl: 'https://secretsoverflow.com/',
  /**
   * Where questions about the night go (James, 2026-09-15). This is the venue's
   * inbox, not the family's: anything about a reservation or a refund made on
   * this site still goes to the contactEmail in settings, because that is the
   * merchant of record and the resort cannot answer it.
   */
  questionsEmail: 'info@secretsfl.com',

  donationUrl: 'https://gofund.me/633a26073',
} as const;

export const OG_IMAGE = {
  url: `${SITE_URL}/og-image.jpg`,
  type: 'image/jpeg',
  width: 1200,
  height: 630,
  alt: 'Brandon Taylor. Celebration of Life, Saturday October 3, 2026, Secrets Hideaway, Kissimmee. #BrandonStrong',
} as const;

export type JsonLd = Record<string, unknown>;

export interface SeoPage {
  readonly path: string;
  /** Page part of the title; empty for the home page (site name only). */
  readonly title: string;
  readonly description: string;
  readonly jsonLd: readonly JsonLd[];
}

const venuePlace: JsonLd = {
  '@type': 'Place',
  name: EVENT.venue.name,
  address: {
    '@type': 'PostalAddress',
    streetAddress: EVENT.venue.street,
    addressLocality: EVENT.venue.city,
    addressRegion: EVENT.venue.region,
    postalCode: EVENT.venue.postalCode,
    addressCountry: 'US',
  },
};

/**
 * Still `EventScheduled`: the venue moved, the event did not move online, was
 * not rescheduled and was not cancelled, and the other three status values all
 * say something untrue. No `endDate` either; see EVENT.start.
 */
const eventLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: EVENT.name,
  description:
    'An evening celebrating Brandon Taylor at Secrets Hideaway. Come in through the Tiki bar: free entry from 7 to 10 PM with a wristband on arrival, and the night carries on after that. There will be food. Rockstar dress code.',
  startDate: EVENT.start,
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  location: venuePlace,
  image: [OG_IMAGE.url],
  url: `${SITE_URL}/event`,
  organizer: { '@type': 'Organization', name: "Brandon's family", url: SITE_URL },
  offers: [
    {
      '@type': 'Offer',
      name: 'Free entry to the celebration, 7 to 10 PM, in through the Tiki bar',
      price: '0',
      priceCurrency: 'USD',
      url: `${SITE_URL}/rsvp`,
      availability: 'https://schema.org/InStock',
      validFrom: '2026-09-15',
    },
  ],
  keywords: 'BrandonStrong',
};

const websiteLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  inLanguage: 'en-US',
};

const donateLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Donate',
  url: `${SITE_URL}/donate`,
  description: 'Support Shawna through the GoFundMe set up for Brandon.',
  potentialAction: {
    '@type': 'DonateAction',
    name: 'Donate to the family',
    target: EVENT.donationUrl,
  },
};

const photosLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ImageGallery',
  name: 'Photos of Brandon',
  url: `${SITE_URL}/photos`,
  description: 'Photos of Brandon Taylor from his family and friends.',
};

const guestbookLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Guestbook',
  url: `${SITE_URL}/guestbook`,
  description: 'Messages for Brandon and the family from the people who love him.',
};

const contactLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact & FAQ',
  url: `${SITE_URL}/contact`,
  about: venuePlace,
};

const updateLd: JsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'An update from the family',
  url: `${SITE_URL}/update`,
  description: 'Why the celebration moved, and everything that changed with it.',
};

export const SEO = {
  home: { path: '/', title: '', description: DEFAULT_DESCRIPTION, jsonLd: [websiteLd, eventLd] },
  update: {
    path: '/update',
    title: 'An update from the family',
    description:
      "Brandon's Celebration of Life has moved to Secrets Hideaway. What changed, and what it means if you had booked a room.",
    jsonLd: [updateLd],
  },
  event: {
    path: '/event',
    title: 'The Event',
    description: 'When, where, what to wear, and what to expect at the celebration of life.',
    jsonLd: [eventLd],
  },
  photos: {
    path: '/photos',
    title: 'Photos',
    description: 'Photos of Brandon from his family and friends, across the years.',
    jsonLd: [photosLd],
  },
  guestbook: {
    path: '/guestbook',
    title: 'Guestbook',
    description:
      'Leave a message for Brandon and the family, and read what the people who love him have written.',
    jsonLd: [guestbookLd],
  },
  // Kept at /rooms so every link, email and QR code already out in the world
  // still lands somewhere useful; the page behind it is now accommodation.
  rooms: {
    path: '/rooms',
    title: 'Where to stay',
    description:
      'Secrets Hideaway is sold out. Book an overflow room at a hotel nearby through SecretsOverflow.com.',
    jsonLd: [],
  },
  rsvp: {
    path: '/rsvp',
    title: 'RSVP',
    description:
      'Coming to the celebration of life? RSVP with the names of your party and, if you like, a donation to Shawna.',
    jsonLd: [eventLd],
  },
  donate: {
    path: '/donate',
    title: 'Donate',
    description: 'Support Shawna through the GoFundMe set up for Brandon.',
    jsonLd: [donateLd],
  },
  contact: {
    path: '/contact',
    title: 'Contact & FAQ',
    description: 'How to reach the family, and answers to common questions about the celebration.',
    jsonLd: [contactLd],
  },
  terms: {
    path: '/terms',
    title: 'Terms',
    description:
      'Terms for the celebration, and what happened to the room reservations made on this site.',
    jsonLd: [],
  },
  privacy: {
    path: '/privacy',
    title: 'Privacy',
    description: 'What this site keeps about an RSVP or a reservation, and what it never stores.',
    jsonLd: [],
  },
} as const satisfies Record<string, SeoPage>;

/** Every public route that gets its own prerendered shell. */
export const PAGES: readonly SeoPage[] = Object.values(SEO);

export function fullTitle(title: string): string {
  return title ? `${title} · ${SITE_NAME}` : SITE_NAME;
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function meta(key: 'name' | 'property', name: string, content: string): string {
  return `<meta ${key}="${name}" content="${escapeAttr(content)}" />`;
}

/** JSON inside a <script> must never be able to close the tag. */
function jsonLdScript(block: JsonLd): string {
  const json = JSON.stringify(block).replace(/</g, '\\u003c');
  return `<script type="application/ld+json">${json}</script>`;
}

/** The head tags for one route, as the static shell carries them. */
export function renderHead(page: SeoPage): string {
  const url = `${SITE_URL}${page.path}`;
  const title = fullTitle(page.title);
  return [
    `<title>${escapeAttr(title)}</title>`,
    meta('name', 'description', page.description),
    `<link rel="canonical" href="${url}" />`,
    meta('name', 'robots', 'index, follow'),
    meta('property', 'og:type', 'website'),
    meta('property', 'og:site_name', SITE_NAME),
    meta('property', 'og:locale', 'en_US'),
    meta('property', 'og:title', title),
    meta('property', 'og:description', page.description),
    meta('property', 'og:url', url),
    meta('property', 'og:image', OG_IMAGE.url),
    meta('property', 'og:image:type', OG_IMAGE.type),
    meta('property', 'og:image:width', String(OG_IMAGE.width)),
    meta('property', 'og:image:height', String(OG_IMAGE.height)),
    meta('property', 'og:image:alt', OG_IMAGE.alt),
    meta('name', 'twitter:card', 'summary_large_image'),
    meta('name', 'twitter:title', title),
    meta('name', 'twitter:description', page.description),
    meta('name', 'twitter:image', OG_IMAGE.url),
    meta('name', 'twitter:image:alt', OG_IMAGE.alt),
    ...page.jsonLd.map(jsonLdScript),
  ].join('\n    ');
}
